/*
  [PELS] Car Bed BOGO
  nextdev-page-car-bed-bogo.js
  1. Hero video: plays muted while on screen and pauses off screen. With reduced motion turned on,
     it stays paused and shows controls instead.
  2. Ranking slider: native scroll snap for touch, looping arrows, mouse drag, and autoplay
     (pauses on hover, focus, off screen and with reduced motion), like the Instant slider.
  3. FAQ: one answer open at a time (also for browsers without exclusive details support).
  4. Sticky button: shows once the hero is off screen and hides while an offer button is on screen.
*/
(function () {
  'use strict';

  var NS = 'nextdev-cbb';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- 1. Video ---------- */
  var videoObserver = null;

  function getVideoObserver() {
    if (videoObserver || !hasIO) return videoObserver;
    videoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.25 });
    return videoObserver;
  }

  function initVideo(v) {
    if (v.getAttribute('data-' + NS + '-ready') === 'true') return;
    v.setAttribute('data-' + NS + '-ready', 'true');
    v.muted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');

    if (reduceMotion) {
      v.removeAttribute('autoplay');
      v.pause();
      v.controls = true;
      return;
    }

    var io = getVideoObserver();
    if (io) {
      v.removeAttribute('autoplay');
      v.pause();
      io.observe(v);
    }
  }

  /* ---------- 2. Slider ---------- */
  function initSlider(root) {
    if (root.getAttribute('data-' + NS + '-ready') === 'true') return;
    var track = root.querySelector('[data-' + NS + '-track]');
    if (!track) return;
    root.setAttribute('data-' + NS + '-ready', 'true');

    var prev = root.querySelector('[data-' + NS + '-prev]');
    var next = root.querySelector('[data-' + NS + '-next]');
    var behavior = reduceMotion ? 'auto' : 'smooth';

    function maxScroll() {
      return Math.max(0, track.scrollWidth - track.clientWidth);
    }

    function step() {
      var slides = track.children;
      if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
      return track.clientWidth;
    }

    function isStatic() {
      return maxScroll() < 2;
    }

    function update() {
      root.classList.toggle('is-static', isStatic());
    }

    function go(direction) {
      var max = maxScroll();
      var x = track.scrollLeft;
      var target;
      if (direction > 0) {
        target = x >= max - 2 ? 0 : Math.min(max, x + step());
      } else {
        target = x <= 2 ? max : Math.max(0, x - step());
      }
      track.scrollTo({ left: target, behavior: behavior });
    }

    if (prev) prev.addEventListener('click', function () { go(-1); restart(); });
    if (next) next.addEventListener('click', function () { go(1); restart(); });

    track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') { event.preventDefault(); go(1); restart(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); go(-1); restart(); }
    });

    /* Mouse drag (touch and pen use native scrolling) */
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    });

    window.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      var delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 4) {
        moved = true;
        track.classList.add('is-dragging');
      }
      if (moved) track.scrollLeft = startScroll - delta;
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      var left = track.scrollLeft;
      track.classList.remove('is-dragging');
      track.scrollLeft = left;
      var s = step();
      if (s > 0) track.scrollTo({ left: Math.round(left / s) * s, behavior: behavior });
      restart();
    }

    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (event) {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);

    /* Autoplay */
    var delay = parseInt(root.getAttribute('data-autoplay'), 10);
    var timer = null;
    var hovered = false;
    var focused = false;
    var onScreen = !hasIO;

    function canPlay() {
      return delay > 0 && !reduceMotion && !hovered && !focused && onScreen && !dragging &&
        !document.hidden && !isStatic();
    }

    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    function restart() {
      stop();
      if (canPlay()) timer = window.setInterval(function () { if (canPlay()) go(1); }, delay);
    }

    if (delay > 0 && !reduceMotion) {
      root.addEventListener('mouseenter', function () { hovered = true; stop(); });
      root.addEventListener('mouseleave', function () { hovered = false; restart(); });
      root.addEventListener('focusin', function () { focused = true; stop(); });
      root.addEventListener('focusout', function (event) {
        if (!root.contains(event.relatedTarget)) { focused = false; restart(); }
      });
      track.addEventListener('touchstart', function () { stop(); }, { passive: true });
      track.addEventListener('touchend', function () { restart(); }, { passive: true });
      document.addEventListener('visibilitychange', restart);
      if (hasIO) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
          restart();
        }, { threshold: 0.2 }).observe(root);
      }
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { update(); restart(); }).observe(track);
    } else {
      window.addEventListener('resize', function () { update(); restart(); });
    }
    update();
    restart();
  }

  /* ---------- 3. FAQ ---------- */
  function initFaq(list) {
    if (list.getAttribute('data-' + NS + '-ready') === 'true') return;
    list.setAttribute('data-' + NS + '-ready', 'true');
    if (!list.hasAttribute('data-single')) return;
    var items = list.querySelectorAll('details');
    Array.prototype.forEach.call(items, function (item) {
      item.addEventListener('toggle', function () {
        if (!item.open) return;
        Array.prototype.forEach.call(items, function (other) {
          if (other !== item && other.open) other.open = false;
        });
      });
    });
  }

  /* ---------- 4. Sticky button ---------- */
  function initSticky(root) {
    var sticky = root.querySelector('[data-' + NS + '-sticky]');
    if (!sticky || sticky.getAttribute('data-' + NS + '-ready') === 'true') return;
    sticky.setAttribute('data-' + NS + '-ready', 'true');
    var link = sticky.querySelector('a');

    function setHidden(hide) {
      sticky.classList.toggle('is-hidden', hide);
      sticky.setAttribute('aria-hidden', hide ? 'true' : 'false');
      if (!link) return;
      if (hide) link.setAttribute('tabindex', '-1');
      else link.removeAttribute('tabindex');
    }

    if (!hasIO) { setHidden(false); return; }

    var watched = [];
    var hero = root.querySelector('[data-' + NS + '-hero]');
    if (hero) watched.push(hero);
    if (sticky.hasAttribute('data-hide-near-cta')) {
      Array.prototype.push.apply(watched, root.querySelectorAll('[data-' + NS + '-cta]'));
    }
    if (!watched.length) { setHidden(false); return; }

    var visible = new Set();
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      setHidden(visible.size > 0);
    }, { threshold: 0.01 });
    watched.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Init ---------- */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('.' + NS);
    if (scope && scope.classList && scope.classList.contains(NS)) roots = [scope];
    Array.prototype.forEach.call(roots, function (root) {
      Array.prototype.forEach.call(root.querySelectorAll('.' + NS + '__video'), initVideo);
      Array.prototype.forEach.call(root.querySelectorAll('[data-' + NS + '-slider]'), initSlider);
      Array.prototype.forEach.call(root.querySelectorAll('[data-' + NS + '-faq]'), initFaq);
      initSticky(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor: re-init when the section is added or reloaded */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
